"use client";

import { useEffect, useRef } from "react";
import { useSession } from "@/lib/auth-react";
import { setToastListener } from "@/lib/notify";
import { startReplay, stopReplay, recordReplayEvent } from "@/lib/replay";

/**
 * Drives session replay: records only while authenticated AND when capture is
 * enabled site-wide (the `replay_enabled` platform setting, resolved server-side
 * into the site-status snapshot). Starts once the session hydrates (has a user
 * id) and restarts cleanly when the identity changes (login-as-someone-else /
 * impersonation). Also feeds toasts into the replay timeline via the notify
 * chokepoint. Renders nothing.
 */
export default function ReplaySession({ enabled = false }: { enabled?: boolean }) {
  const { data: session } = useSession();
  const userId = session?.user?.id;

  // Always-current token for the recorder's flushes (survives slides/refresh).
  const tokenRef = useRef<string | undefined>(undefined);
  tokenRef.current = (session as { accessToken?: string } | null)?.accessToken;

  useEffect(() => {
    // Capture is a site-wide kill-switch: no recording unless explicitly on.
    if (!enabled || !userId) {
      stopReplay();
      return;
    }
    // startReplay is idempotent per-user and restarts itself on identity change.
    startReplay({ userId, getToken: () => tokenRef.current });
    // Intentionally no cleanup on userId change — recording continues across
    // route changes; the identity guard inside startReplay handles switches.
  }, [enabled, userId]);

  // Toast timeline events (single global listener via @/lib/notify).
  useEffect(() => {
    setToastListener((kind, message) =>
      recordReplayEvent("toast", {
        summary: `${kind}: ${message}`.slice(0, 200),
      }),
    );
    return () => setToastListener(null);
  }, []);

  // Stop on teardown (app unmount / hard logout).
  useEffect(() => () => stopReplay(), []);

  return null;
}
